import { fromJS, is } from 'immutable';
import { isUndefined } from 'lodash';
import {
  all,
  call,
  delay,
  put,
  select,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { scrollToElement } from 'src/utils/domUtils';

// Constants:
import { PREVIOUS_FILTERS } from 'src/store/previousFilters/constants';
import {
  pushExploreExploreLoadingMoreFinish,
  pushExploreLoadingMoreError,
  pushExploreLoadingMoreStart,
  pushExploreSortChanged,
} from 'src/routes/SeoApp/utils/gtmEvents';
import { getCurrencyCode, getIsMobile } from 'src/store/view/selectors';
import { setPreviousFiltersAction } from 'src/store/previousFilters/actions';
import {
  setIsPreviousFiltersShown,
  setPreviousFiltersToSlice,
} from 'src/store/previousFilters/slice';
import { getLastAppliedFilters } from '../mobile/selectors';
import { SHOW_MORE } from '../hotels/constants';
import { CHANGE_SORTING } from '../sorting/constants';
import { INIT_APP, TOGGLE_FILTERS_PANEL } from '../view/constants';
import {
  APPLY_FILTERS,
  CANCEL_FILTERS,
  CHANGE_FILTER,
  CLEAR_FILTER,
  CLEAR_FILTERS,
  SET_FILTERS,
  SET_PREVIOUS_FILTERS,
} from './constants';

// Selectors:
import { getFilter, getFilters, getOfferFilters } from './selectors';
import {
  getAdults,
  getDestinationId,
  getKids,
  getParams,
  getPresenterParams,
  getSearchUrl,
} from '../searcher/selectors';
import { isFiltersHidden } from '../view/selectors';

import {
  getCurrentPage,
  getLiveSearchAllowed,
  getPageLimit,
  latestVisibleHotelSelector,
} from '../hotels/selectors';

// Sagas:
import { getSortParams } from '../sorting/sagas';

// Actions:
import {
  paginationSuccessAction,
  setCurrentVisibleHotelsAction,
} from '../hotels/actions';
import { saveFiltersForCancelAction } from '../mobile/actions';
import { toggleFiltersAction } from '../view/actions';
import { filtrationSuccessAction, setFiltersAction } from './actions';

// Utils:
import { getUrlParams } from '../../../../../utils/uriUtils';
import { filterDataToURL } from '../../../data/controllers/FilterController';
import { getFiltersFromUrl, updateURL, uri } from '../../../utils';
import { buildHotel } from '../../../utils/buildUtils';

// Api:
import { api } from '../../../../../api';

const FILTER_DELAY_TIME = 1500; // 1.5s
const IMMEDIATE_FILTERS = ['prices', 'hotel_name'];

function* delayAction(action) {
  const { type, payload = {} } = action;
  const path = payload.path || payload;
  if (type === CHANGE_FILTER && IMMEDIATE_FILTERS.indexOf(path) === -1) {
    yield delay(FILTER_DELAY_TIME);
  }
}

function* scrollToPosition(newHotelsCount) {
  const latestVisibleHotel = yield select(latestVisibleHotelSelector);

  if (newHotelsCount < latestVisibleHotel || !latestVisibleHotel) {
    scrollToElement(document.body);
    yield put(setCurrentVisibleHotelsAction([0]));
  }
}

export function* getFiltersParams() {
  const filtersData = yield select(getFilters);

  return filtersData.keySeq().reduce((result, filterName) => {
    const filterData = filtersData.get(filterName);
    if (filterData) {
      const filterParams = filterDataToURL(filterName, fromJS(filterData));
      Object.keys(filterParams).forEach((param) => {
        const val = filterParams[param];
        if (val) {
          // eslint-disable-next-line no-param-reassign
          result[param] = val;
        }
      });
    }
    return result;
  }, {});
}

export function* getFilteredResults(withPagination = false) {
  const pageLimit = yield select(getPageLimit);
  const liveSearchAllowed = yield select(getLiveSearchAllowed);

  const urlParams = getUrlParams();
  const filters = yield getFiltersParams();
  const sorting = yield getSortParams();
  const presenterParams = yield select(getPresenterParams);

  const currentPage = withPagination ? yield select(getCurrentPage) : 0;

  const params = {
    ...urlParams,
    ...filters,
    ...sorting,
    search_params: presenterParams,
    page_number: currentPage,
    page_limit: pageLimit,
    live_search_allowed: liveSearchAllowed,
  };

  const resp = yield api.postExploreGroupedHotels(params);
  return resp;
}

function* fetchFilteredData() {
  try {
    const isMobile = yield select(getIsMobile);
    const resp = yield call(getFilteredResults);
    const { allowed_filters, hotels, filtered_hotels, hotels_count } = resp;
    const searchParams = yield select(getParams);
    const currencyCode = yield select(getCurrencyCode);
    const filters = yield select(getOfferFilters);

    const resultHotels = hotels.map((hotel) =>
      buildHotel(hotel, searchParams, currencyCode, filters),
    );

    yield put(
      filtrationSuccessAction({
        filters: allowed_filters,
        hotels: resultHotels,
        filtered_hotels,
        hotelsCount: hotels_count,
      }),
    );
    if (!isMobile) yield scrollToPosition(hotels_count);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
}

function* handlePagination() {
  const liveSearchAllowed = yield select(getLiveSearchAllowed);

  if (liveSearchAllowed) {
    const link = yield select(getSearchUrl);
    window.location.href = `/search/${link}`;
  } else {
    yield fetchDataPagination();
  }
}

function* fetchDataPagination() {
  try {
    pushExploreLoadingMoreStart();

    const resp = yield call(getFilteredResults, true);
    const { hotels } = resp;

    const searchParams = yield select(getParams);
    const currencyCode = yield select(getCurrencyCode);
    const filters = yield select(getOfferFilters);

    const resultHotels = hotels.map((hotel) =>
      buildHotel(hotel, searchParams, currencyCode, filters),
    );

    yield put(paginationSuccessAction({ resultHotels }));
    pushExploreExploreLoadingMoreFinish();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    pushExploreLoadingMoreError({ error: error.message });
  }
}

function* fetchDataWithDelay(action) {
  yield delayAction(action);
  yield call(fetchFilteredData);
}

function* fetchData(action) {
  if (action.payload === SHOW_MORE) {
    yield call(fetchDataPagination);
  } else {
    yield call(fetchFilteredData);
  }
}

export function* updateFilterUriParam(filterName) {
  const filterData = yield select(getFilter, filterName);
  if (filterData) {
    const filterParams = filterDataToURL(filterName, fromJS(filterData));
    uri.removeSearch(Object.keys(filterParams));
    Object.keys(filterParams).forEach((param) => {
      const val = filterParams[param];
      if (val) {
        uri.addSearch(param, val);
      }
    });
  }
}

function* changeURL(action) {
  try {
    const path = action.payload.path || action.payload;
    yield updateFilterUriParam(path);
    updateURL();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* stashFilters(action) {
  try {
    yield delayAction(action);
    const activeFilters = Object.keys(getFiltersFromUrl());
    const filtersData = yield select(getFilters);

    const stashedFilters = filtersData.filter((val, key) => {
      if (key === 'prices') {
        return ['filter_price_min', 'filter_price_max'].some(
          (name) => activeFilters.indexOf(name) !== -1,
        );
      }
      return activeFilters.indexOf(`filter_${key}`) !== -1;
    });

    if (!Object.keys(stashedFilters).length) return;
    const destinationId = yield select(getDestinationId);
    const adults = yield select(getAdults);
    const kids = yield select(getKids);
    yield sessionStorage.setItem(
      PREVIOUS_FILTERS,
      JSON.stringify({
        filters: stashedFilters,
        destinationId,
        adults,
        kids,
      }),
    );
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* saveFiltersForCancel(action) {
  try {
    const { payload } = action;
    const showFilters = isUndefined(payload)
      ? !(yield select(isFiltersHidden))
      : payload;
    if (showFilters) {
      // save filters when filters pannel toggled to show
      const currentFilters = yield select(getFilters);
      yield put(saveFiltersForCancelAction(currentFilters));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* buildURL() {
  try {
    const activeFilters = Object.keys(getFiltersFromUrl());
    if (activeFilters.length) {
      uri.removeSearch(activeFilters);
    }
    const filtersData = yield select(getFilters);
    const [...filters] = filtersData.keys();
    yield filters.map(updateFilterUriParam);
    updateURL();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* cancelFiltersChange() {
  try {
    const lastAppliedFilters = yield select(getLastAppliedFilters);
    const currentFilters = yield select(getFilters);
    if (!is(lastAppliedFilters, currentFilters)) {
      yield put(setFiltersAction(lastAppliedFilters));
    }
    yield put(toggleFiltersAction());
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Произошла ошибка', error);
  }
}

function* handleGetPreviousFilters() {
  try {
    const state = yield JSON.parse(
      sessionStorage.getItem(PREVIOUS_FILTERS) || '{}',
    );
    yield put(setPreviousFiltersToSlice(state));
    if (!Object.keys(state).length) {
      yield put(setIsPreviousFiltersShown(false));
      return;
    }
    const currentDestinationId = yield select(getDestinationId);
    const currentAdults = yield select(getAdults);
    const currentKids = yield select(getKids);

    if (
      state.destinationId === currentDestinationId &&
      state.adults === currentAdults &&
      JSON.stringify(state.kids) === JSON.stringify(currentKids)
    ) {
      yield put(setPreviousFiltersAction(state.filters));
    } else yield put(setIsPreviousFiltersShown(true));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('e', e);
  }
}

function addPreviousToURL(action) {
  const filters = action.payload;

  Object.keys(filters).forEach((filterName) => {
    const filterData = filters[filterName];

    const params = filterDataToURL(filterName, fromJS(filterData));
    Object.keys(params).forEach((param) => {
      const val = params[param];
      if (val) {
        uri.addSearch(param, val);
      }
    });
  });
  updateURL();
}

function* applyFilters(action) {
  yield stashFilters(action);
  yield delay(100);
  yield put(toggleFiltersAction(false));
}

export default function* filterSaga() {
  const isMobile = yield select(getIsMobile);
  const sagas = [
    // On each request:
    yield takeEvery(CHANGE_FILTER, changeURL),
    yield takeEvery(CLEAR_FILTER, changeURL),
    yield takeEvery(CLEAR_FILTERS, buildURL),
    yield takeEvery(SET_FILTERS, buildURL),
    yield takeEvery(SET_PREVIOUS_FILTERS, addPreviousToURL),

    // Latest:
    yield takeLatest(CHANGE_FILTER, fetchDataWithDelay),
    yield takeLatest(CHANGE_SORTING, fetchData),
    yield takeLatest(INIT_APP, handleGetPreviousFilters),
    yield takeLatest(CHANGE_SORTING, (action) => {
      pushExploreSortChanged({ type: action.payload.order });
    }),
    yield takeLatest(CLEAR_FILTER, fetchData),
    yield takeLatest(CLEAR_FILTERS, fetchData),
    yield takeLatest(SET_PREVIOUS_FILTERS, fetchData),
    yield takeLatest(SET_FILTERS, fetchData),
    yield takeLatest(SHOW_MORE, handlePagination),
    yield takeLatest([CLEAR_FILTER, CHANGE_FILTER], stashFilters),
  ];
  if (isMobile) {
    sagas.concat([
      yield takeLatest(TOGGLE_FILTERS_PANEL, saveFiltersForCancel),
      yield takeLatest(APPLY_FILTERS, applyFilters),
      yield takeLatest(CANCEL_FILTERS, cancelFiltersChange),
    ]);
  }

  yield all(sagas);
}
